import React, {useContext} from 'react';
import {AppContext} from '../../contexts/AppContext';
import Skeleton from 'react-loading-skeleton';
import {translate} from '../../helpers/base';

const FilterPill = ({filterType, filterValue, label, onRemove}) => (
    <label className="filter-pill active" key={`${filterType}-${filterValue}`}>
        {label}
        <span className="close" onClick={() => onRemove(filterType, filterValue)}></span>
    </label>
);

const includedTypes = [
    'accoFeatures',
    'accoType',
    'animals',
    'awards',
    'experienceWorlds',
    'locations',
    'region',
    'mealPlans',
    'roomTypes',
    'price',
    'specializations',
    'topics',
    'petsAllowed',
    'accessible',
    'distance',
    'q'
];

export const useHasActiveFilters = () => {
    const {selectedFilters} = useContext(AppContext);
    if (selectedFilters.accoType) {
        return true;
    }
    return Object.keys(selectedFilters).some(filterType => {
        const filterValues = selectedFilters[filterType] ?? [];
        const values = Array.isArray(filterValues) ? filterValues : [filterValues];
        return includedTypes.includes(filterType) && values.length > 0;
    });
};

const ActiveFilters = () => {

    const {
        selectedFilters,
        handleFilterChange,
        handleAccommodationTypeChange,
        isLoading,
        allFilters: filters
    } = useContext(AppContext);

    const handleRemoveFilter = (filterType, filterValue) => {
        if (filterType === 'accoType') {
            handleAccommodationTypeChange(null);
        } else {
            handleFilterChange(filterType, filterValue, false);
        }
    };

    const getFilterLabel = (filterType, filterValue) => {
        const filterOption = filters.find(item => item.id === filterValue);
        let label = filterOption?.label || filterValue;

        // Extract first part of location if applicable
        if (filterType === 'q' && label.includes(',')) {
            label = label.split(',')[0];
        }

        // Handle price filter label
        if (filterType === 'price' && label.includes('-')) {
            const [minPrice, maxPrice] = label.split('-').map(price => price.trim());
            return `€ ${minPrice} - ${maxPrice}`;
        }

        if (filterType === 'petsAllowed') {
            return translate('accommodation-overview.filter.misc.petsAllowed');
        }

        if (filterType === 'accessible') {
            return translate('accommodation-overview.filter.misc.accessibleFacilities');
        }

        if (filterType === 'distance') {
            return `${label} km`;
        }

        return label;
    };

    const renderFilterPills = (includedTypes = []) => {
        const filterPills = [];
        const regionLabels = new Set();

        // Collect all region labels
        if (selectedFilters.region) {
            const regionValues = Array.isArray(selectedFilters.region)
                ? selectedFilters.region
                : [selectedFilters.region];

            regionValues.forEach(regionValue => {
                const regionLabel = getFilterLabel('region', regionValue);
                if (regionLabel) {
                    regionLabels.add(regionLabel);
                }
            });
        }

        Object.keys(selectedFilters).reverse().forEach(filterType => {
            const filterValues = selectedFilters[filterType];

            if (!includedTypes.includes(filterType)) {
                return;
            }

            if (filterValues) {
                const values = Array.isArray(filterValues) ? filterValues : [filterValues];
                values.forEach(filterValue => {
                    const filterLabel = getFilterLabel(filterType, filterValue);
                    if (filterLabel) {

                        // Exclude pill for 'q' if its label matches a region label
                        if (filterType === 'q' && regionLabels.has(filterLabel)) {
                            return;
                        }

                        filterPills.push(
                            <FilterPill
                                key={`${filterType}-${filterValue}`}
                                filterType={filterType}
                                filterValue={filterValue}
                                label={filterLabel}
                                onRemove={handleRemoveFilter}
                            />
                        );
                    }
                });
            }
        });

        return filterPills;
    };

    if (isLoading) {
        return (
            <Skeleton
                count={2}
                inline={true}
                width={150}
                height={40}
                borderRadius={20}
                style={{marginRight: 10}}
            />
        );
    }

    return renderFilterPills(includedTypes);
};

export default ActiveFilters;